/**
 * Blog SC Schindler – Beitragsseite (sc-blog, 18.10.2026)
 *
 * <scb-artikel>  Lesefortschritt (gelber Balken oben), aktive Markierung im
 *                Inhaltsverzeichnis, mobiles Verzeichnis nach dem Sprung
 *                einklappen, breite Tabellen nur dann fokussierbar machen,
 *                wenn sie wirklich seitlich scrollen, „Link kopieren“.
 *
 * Das Verzeichnis selbst rendert Liquid (sections/sc-blog-beitrag.liquid);
 * es funktioniert auch ohne JavaScript. Keine Abhängigkeiten.
 */

(() => {
  if (customElements.get('scb-artikel')) return;

  const clamp01 = (value) => Math.min(1, Math.max(0, value));

  class ScbArtikel extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.frame = 0;
      this.activeId = '';
      this.prose = this.querySelector('[data-scb-prose]');
      this.bar = this.querySelector('[data-scb-fortschritt]');
      this.links = Array.from(this.querySelectorAll('a[data-scb-toc-link]'));

      const ids = [...new Set(this.links.map((link) => decodeURIComponent(link.hash.slice(1))))];
      this.headings = ids.map((id) => document.getElementById(id)).filter((el) => el instanceof HTMLElement);

      const schedule = () => {
        if (this.frame) return;
        this.frame = window.requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
      };

      document.addEventListener('scroll', schedule, { passive: true, capture: true, signal });
      window.addEventListener('resize', schedule, { passive: true, signal });

      const syncTables = () => {
        for (const box of this.querySelectorAll('.scb-tabelle')) {
          if (box.scrollWidth > box.clientWidth + 1) {
            box.setAttribute('role', 'region');
            box.setAttribute('tabindex', '0');
            box.setAttribute('aria-label', box.dataset.label || '');
          } else {
            box.removeAttribute('role');
            box.removeAttribute('tabindex');
            box.removeAttribute('aria-label');
          }
        }
      };
      syncTables();
      window.addEventListener('resize', syncTables, { passive: true, signal });

      this.addEventListener(
        'click',
        (event) => {
          const target = event.target instanceof Element ? event.target : null;
          if (!target) return;

          const tocLink = target.closest('a[data-scb-toc-link]');
          if (tocLink) {
            const details = tocLink.closest('details');
            if (details instanceof HTMLDetailsElement) details.open = false;
            return;
          }

          const copy = target.closest('[data-scb-copy]');
          if (copy instanceof HTMLElement) this.copy(copy);
        },
        { signal }
      );

      this.update();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      if (this.frame) window.cancelAnimationFrame(this.frame);
      this.frame = 0;
      window.clearTimeout(this.copyTimer);
    }

    update() {
      const viewport = window.innerHeight || document.documentElement.clientHeight;

      if (this.bar && this.prose) {
        const rect = this.prose.getBoundingClientRect();
        const distance = rect.height - viewport * 0.5;
        const progress =
          distance > 0 ? clamp01((viewport * 0.5 - rect.top) / distance) : rect.top < 0 ? 1 : 0;
        this.bar.style.transform = `scaleX(${progress.toFixed(4)})`;
      }

      if (!this.headings.length) return;

      const line = Math.min(viewport * 0.3, 240);
      let activeId = '';
      for (const heading of this.headings) {
        if (heading.getBoundingClientRect().top - line <= 0) activeId = heading.id;
        else break;
      }

      if (activeId === this.activeId) return;
      this.activeId = activeId;

      for (const link of this.links) {
        const isActive = activeId !== '' && decodeURIComponent(link.hash.slice(1)) === activeId;
        link.classList.toggle('is-active', isActive);
        if (isActive) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      }
    }

    async copy(button) {
      const url = button.dataset.scbCopy || window.location.href;
      let copied = false;
      try {
        await navigator.clipboard.writeText(url);
        copied = true;
      } catch {
        const field = document.createElement('textarea');
        field.value = url;
        field.setAttribute('readonly', '');
        field.style.position = 'fixed';
        field.style.opacity = '0';
        document.body.append(field);
        field.select();
        try {
          copied = document.execCommand('copy');
        } catch {
          copied = false;
        }
        field.remove();
      }
      if (!copied) return;

      const status = this.querySelector('[data-scb-copy-status]');
      if (status) status.textContent = button.dataset.copied || '';
      button.classList.add('is-copied');
      window.clearTimeout(this.copyTimer);
      this.copyTimer = window.setTimeout(() => {
        button.classList.remove('is-copied');
        if (status) status.textContent = '';
      }, 2200);
    }
  }

  customElements.define('scb-artikel', ScbArtikel);
})();
